import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  fontFamily,
  fontSize,
  fontWeight,
  spacing,
  letterSpacing,
  interactive,
  nav,
  surface,
  text,
  ThemeProvider,
} from "@atlas-int/tokens";
import { registry, type ComponentEntry } from "./registry";
import TokensSection from "./sections/TokensSection";
import ButtonSection from "./sections/ButtonSection";
import DataChipSection from "./sections/DataChipSection";
import DividerSection from "./sections/DividerSection";
import ExpandableItemSection from "./sections/ExpandableItemSection";
import ModalSection from "./sections/ModalSection";
import VizComponentsSection from "./sections/VizComponentsSection";
import ControlsSection from "./sections/ControlsSection";
import ButtonVariantsSection from "./sections/ButtonVariantsSection";
import NavigationSection from "./sections/NavigationSection";
import LayoutSection from "./sections/LayoutSection";
import TypographySection from "./sections/TypographySection";
import FooterSection from "./sections/FooterSection";
import StaticPageSection from "./sections/StaticPageSection";
import MixinsSection from "./sections/MixinsSection";
import UtilityComponentsSection from "./sections/UtilityComponentsSection";

const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  font-family: ${fontFamily.base};
  background-color: ${surface.page};
`;

const Sidebar = styled.nav`
  width: 240px;
  min-width: 240px;
  height: 100vh;
  overflow-y: auto;
  background-color: ${nav.background};
  color: ${text.onDark};
  padding: ${spacing[10]} 0;
  position: fixed;
  left: 0;
  top: 0;
`;

const SidebarTitle = styled.h1`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.bold};
  padding: 0 ${spacing[10]};
  margin: 0 0 ${spacing[10]} 0;
`;

const CategoryLabel = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: ${letterSpacing.normal};
  color: ${text.muted};
  padding: ${spacing[4]} ${spacing[10]};
  margin-top: ${spacing[4]};
`;

const SidebarLink = styled.a<{ $active?: boolean }>`
  display: block;
  padding: ${spacing[2]} ${spacing[10]} ${spacing[2]} ${spacing[12]};
  color: ${({ $active }) => ($active ? text.onDark : interactive.muted)};
  background-color: ${({ $active }) => ($active ? nav.hover : "transparent")};
  text-decoration: none;
  font-size: ${fontSize.base};
  border-radius: 0;

  &:hover {
    background-color: ${nav.hover};
    color: ${text.onDark};
  }
`;

const Content = styled.main`
  flex: 1;
  margin-left: 240px;
  overflow-y: auto;
  padding: ${spacing[16]} ${spacing[20]};
`;

const PageHeader = styled.div`
  margin-bottom: ${spacing[16]};
`;

const Title = styled.h1`
  font-size: ${fontSize["3xl"]};
  font-weight: ${fontWeight.bold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Subtitle = styled.p`
  font-size: ${fontSize.lg};
  color: ${text.secondary};
  margin: 0;
`;

// Group registry items by category
const groupedRegistry = registry.reduce<Record<string, ComponentEntry[]>>((acc, entry) => {
  if (!acc[entry.category]) acc[entry.category] = [];
  acc[entry.category].push(entry);
  return acc;
}, {});

const categoryLabels: Record<string, string> = {
  foundation: "Foundation",
  ui: "UI Components",
  visualization: "Visualization",
};

const AtlasUIShowcase: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>(registry[0]?.id ?? "");

  const handleClick = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <ThemeProvider theme="light">
      <PageContainer>
        <Sidebar>
          <SidebarTitle>Atlas UI</SidebarTitle>
          {Object.entries(groupedRegistry).map(([category, entries]) => (
            <div key={category}>
              <CategoryLabel>{categoryLabels[category] || category}</CategoryLabel>
              {entries.map((entry) => (
                <SidebarLink
                  key={entry.id}
                  href={`#${entry.id}`}
                  $active={activeSection === entry.id}
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick(entry.id);
                  }}
                >
                  {entry.name}
                </SidebarLink>
              ))}
            </div>
          ))}
        </Sidebar>
        <Content>
          <PageHeader>
            <Title>Atlas UI Component Library</Title>
            <Subtitle>
              Design tokens, components, and visualization primitives
              {" — "}
              theme follows your OS preference
            </Subtitle>
          </PageHeader>
          <TokensSection />
          <TypographySection />
          <LayoutSection />
          <MixinsSection />
          <ButtonSection />
          <DataChipSection />
          <DividerSection />
          <ExpandableItemSection />
          <ModalSection />
          <ControlsSection />
          <ButtonVariantsSection />
          <StaticPageSection />
          <NavigationSection />
          <FooterSection />
          <UtilityComponentsSection />
          <VizComponentsSection />
        </Content>
      </PageContainer>
    </ThemeProvider>
  );
};

export default AtlasUIShowcase;
